import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

import { whenBiometricsAsked } from './biometrics';
import { clearUserLogout } from './loginReturn';
import { isStoreBuild } from './platform';
import { canHavePush } from './pushAccess';
import { pushNotificationId, pushTarget } from './pushRoute';
import type { PushPermission } from './pushToggle';
import type { AuthUser } from '@/api/auth';
import { api } from '@/api/client';

/**
 * Пуш-уведомления (договор `docs/CONTRACTS/push.md`, приложение 1.1.1).
 *
 * ⚠️ ДО 27.08.2026 ИХ НЕ БЫЛО ВООБЩЕ: сервер слал в пустоту, приложение не отдавало метку устройства. С 25.09
 * по 01.10.2026 вызов был снят (D73): APNs на сервере не был настроен, а системный вопрос задаётся ОДИН раз
 * за установку — «да» на пустой вопрос сжёг бы его. Сервер на проде с 30.09.2026.
 *
 * КОМУ. Только ученику с куратором (`pushAccess.ts`): пушем уходят «Куратор оценил урок» и «Куратор проверил
 * ДЗ». Бесплатнику, куратору и админу — ни вопроса, ни метки. На сайте модуль не делает ничего.
 *
 * КОГДА СПРАШИВАЕМ. После входа или при первом запуске с живой сессией — системным окном iOS, без своего
 * листа (решение владельца 01.10.2026). Не на экране входа и не до него: вопрос задаётся один раз, и к этому
 * моменту у ученика уже есть курс и куратор. Отказал — дальше только тумблер в профиле и Настройки телефона.
 *
 * ЧТО ДЕЛАЕМ С МЕТКОЙ. Отдаём серверу при КАЖДОМ запуске, а не однажды: система меняет метку при
 * переустановке, восстановлении из копии и иногда просто так. Метка, отданная год назад, давно не работает.
 */

/** Как называется площадка на нашем сервере: он различает apns и fcm. */
function platformName(): 'apns' | 'fcm' | null {
  switch (Capacitor.getPlatform()) {
    case 'ios':
      return 'apns';
    case 'android':
      return 'fcm';
    default:
      return null;
  }
}

/**
 * ⚠️ ПУШИ НА ANDROID ВЫКЛЮЧЕНЫ, ПОКА НЕТ FIREBASE (решение владельца 16.09.2026, техдолг п. 235).
 *
 * Без `google-services.json` в `android/app` сборка молча не подключает плагин Google, и метку
 * устройства выдать нечем. Спросить разрешение при этом можно — и ученик ответил бы «да» на вопрос,
 * за которым ничего не стоит, а второй раз система не спросит, когда пуши заработают по-настоящему.
 * Поэтому на Android не спрашиваем и не регистрируемся вовсе.
 *
 * Включать ВМЕСТЕ с проектом Firebase: файл в `android/app`, учётка FCM на сервере, живая проверка.
 * Сверяет `lib/push.test.ts`.
 */
const ANDROID_PUSH_READY = false;

/** Площадка, на которой пуши сейчас работают. `null` — сайт, Android без Firebase, незнакомая площадка. */
function readyPlatform(): 'apns' | 'fcm' | null {
  if (!isStoreBuild()) return null;
  const platform = platformName();
  if (platform === 'fcm' && !ANDROID_PUSH_READY) return null;
  return platform;
}

/** Для учёта на сервере; тему пуша он берёт из своих настроек (договор §1). */
const BUNDLE_ID = 'kz.onebilim.academy';

let listenersReady = false;

/**
 * Последняя выданная системой метка.
 *
 * Нужна при выходе: сервер удаляет метку по её значению, а не по владельцу, —
 * на одном телефоне может быть несколько аккаунтов, и снести чужую нельзя.
 *
 * ⚠️ КОПИЯ В ХРАНИЛИЩЕ. Метка приходит в обработчик через секунду-другую после `register()`, а в запуске,
 * где регистрации не было (разрешение отозвали в Настройках), не приходит вовсе — без копии выход оставил
 * бы метку на сервере. Метка не секрет: это адрес устройства у Apple, без ключа сервера по ней ничего не отправить.
 */
let lastToken: string | null = null;
const TOKEN_KEY = 'onebilim:push-token';

function rememberToken(token: string | null): void {
  lastToken = token;
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* хранилище недоступно — останется копия в памяти до конца запуска */
  }
}

function knownToken(): string | null {
  if (lastToken) return lastToken;
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Обработчики метки вешаем ОДИН раз за запуск: повторный вход не должен отправлять метку дважды. */
async function ensureRegistrationListeners(platform: 'apns' | 'fcm'): Promise<void> {
  if (listenersReady) return;
  listenersReady = true;

  await PushNotifications.addListener('registration', (token) => {
    // ⚠️ НИЖНИЙ РЕГИСТР. Сервер при записи приводит метку APNs к нижнему регистру, а удаляет по точному
    // совпадению: метка «как пришла» при выходе не нашла бы свою строку — и ответ всё равно был бы 204.
    const value = platform === 'apns' ? token.value.toLowerCase() : token.value;
    rememberToken(value);
    void sendTokenToServer(value, platform);
  });

  await PushNotifications.addListener('registrationError', (err) => {
    // Молчать нельзя: без метки уведомления не придут, а внешне всё
    // выглядит рабочим. Пусть хотя бы останется след.
    console.warn('[push] устройство не зарегистрировалось:', err);
  });
}

let syncing: Promise<void> | null = null;

/**
 * Привести пуши в порядок для вошедшего: спросить разрешение, если ещё не спрашивали, и отдать метку.
 *
 * Зовётся из оболочки ученика (`hooks/usePushSync.ts`) после входа и при каждом запуске с живой сессией.
 * Повторный вызов безопасен: вопрос система задаёт один раз, метка на сервере перезаписывается.
 */
export async function syncPush(user: AuthUser | null | undefined): Promise<void> {
  if (!isStoreBuild()) return;
  const platform = readyPlatform();
  if (!platform) return;
  // ⚠️ Бесплатнику, куратору и админу — ни вопроса, ни метки (решение владельца 01.10.2026).
  if (!canHavePush(user)) return;
  // Один заход за раз: вход и ответ `/auth/me` приходят почти одновременно, двух системных окон быть не должно.
  if (syncing) return syncing;
  syncing = runSync(platform).finally(() => {
    syncing = null;
  });
  return syncing;
}

async function runSync(platform: 'apns' | 'fcm'): Promise<void> {
  try {
    await ensureRegistrationListeners(platform);

    const status = await PushNotifications.checkPermissions();
    if (status.receive === 'denied') {
      // Человек уже отказал. Спрашивать снова система не даст — окно
      // показывается один раз за установку. Молча выходим.
      return;
    }

    if (status.receive !== 'granted') {
      // Сначала наше окно про вход по лицу (первый вход), потом системное: два окна друг на друге — ни одно не прочитают.
      await whenBiometricsAsked();
      const asked = await PushNotifications.requestPermissions();
      if (asked.receive !== 'granted') return;
    }

    // register() запускает выдачу метки: она придёт в обработчик 'registration'
    // асинхронно, иногда через секунду-другую.
    await PushNotifications.register();
  } catch (e) {
    console.warn('[push] не удалось подключить:', e);
  }
}

/** Разрешение системы — для тумблера в настройках. `null` — пуши здесь не работают (сайт, Android). */
export async function pushPermission(): Promise<PushPermission | null> {
  if (!readyPlatform()) return null;
  try {
    return (await PushNotifications.checkPermissions()).receive;
  } catch {
    return null;
  }
}

/**
 * Включение тумблером, когда систему ещё не спрашивали: системное окно, при согласии — метка.
 * Возвращает ответ системы; сбой считаем отказом — тумблер не должен выглядеть включённым без метки.
 */
export async function requestPushFromToggle(): Promise<PushPermission> {
  const platform = readyPlatform();
  if (!platform) return 'denied';
  try {
    await ensureRegistrationListeners(platform);
    const asked = await PushNotifications.requestPermissions();
    if (asked.receive === 'granted') await PushNotifications.register();
    return asked.receive;
  } catch (e) {
    console.warn('[push] не удалось подключить:', e);
    return 'denied';
  }
}

/**
 * Отдать метку устройства серверу.
 *
 * Сбой здесь не должен ничего ломать: человек уже вошёл и пользуется
 * приложением, а отсутствие уведомлений — не повод показывать ему ошибку.
 * Не доехала — доедет при следующем запуске.
 */
async function sendTokenToServer(token: string, platform: 'apns' | 'fcm'): Promise<void> {
  try {
    // `environment` не шлём: пусто — как у сервера (прод — production, стенд — sandbox), договор §1.
    await api.post('/notifications/devices', { token, platform, bundle_id: BUNDLE_ID });
  } catch (e) {
    console.warn('[push] метка устройства не доехала до сервера:', e);
  }
}

/** Сколько ждём снятия метки при выходе: «выйти» обязано сработать и без сети. */
const DISABLE_TIMEOUT_MS = 4_000;

/**
 * Отключить пуши при выходе из аккаунта.
 *
 * ⚠️ ВАЖНО НА ОБЩЕМ ТЕЛЕФОНЕ. Метка привязана к устройству, а не к человеку.
 * Если её не убрать, следующий вошедший будет получать уведомления
 * предыдущего — с названиями его уроков на экране блокировки.
 *
 * ⚠️ ЗВАТЬ ДО СБРОСА СЕССИИ И ДОЖИДАТЬСЯ (01.10.2026). Кнопки «Выйти» делают `await disablePush()` и только
 * потом сбрасывают сессию: запрос идёт обычным путём, с ключом и с продлением, если ключ успел истечь.
 * Прежний вызов без ожидания из `logout()` не работал вовсе: перехватчик запросов асинхронный и читал ключ
 * уже ПОСЛЕ сброса — DELETE уходил без `Authorization`. Не проявлялось, пока меток не было.
 *
 * `accessToken` — для вынужденного выхода из `logout()`, где ждать нельзя: ключ берётся до сброса и
 * кладётся в запрос явно.
 */
export async function disablePush(accessToken?: string | null): Promise<void> {
  if (!isStoreBuild()) return;
  if (!platformName()) return;

  const token = knownToken();
  if (!token) return;
  // Забываем сразу: второй вызов (кнопка, затем `logout()`) не должен слать второй запрос.
  rememberToken(null);

  try {
    await api.delete(`/notifications/devices/${encodeURIComponent(token)}`, {
      timeout: DISABLE_TIMEOUT_MS,
      ...(accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {}),
    });
  } catch {
    // Выход не должен упираться в сеть: человек нажал «выйти» и обязан выйти.
  }
}

/* ───────────────────────── нажатие на пуш и пуш при открытом приложении ───────────────────────── */

type PushRouter = { navigate: (to: string) => unknown };
type PushQueryClient = { invalidateQueries: (filters: { queryKey: readonly unknown[] }) => unknown };
/** Кто сейчас вошёл — читается в момент нажатия, уже после восстановления сессии. */
type PushSession = { ready: () => Promise<void>; user: () => AuthUser | null };

let openReady = false;

/**
 * Подписаться на нажатие и на пуш при открытом приложении. Зовётся из `main.tsx` ДО отрисовки роутера:
 * при холодном старте плагин держит нажатие до первого подписчика — так оно не теряется, когда
 * приложение было убито.
 *
 * Сессию и роутер получает параметрами: модуль не тянет хранилище входа (оно само зависит от этого модуля).
 */
export function initPush(router: PushRouter, queryClient: PushQueryClient, session: PushSession): void {
  if (!isStoreBuild()) return;
  if (!readyPlatform() || openReady) return;
  openReady = true;

  const refreshFeed = () => void queryClient.invalidateQueries({ queryKey: ['notifications'] });

  void (async () => {
    try {
      await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        void openFromPush(action.notification.data, router, session, refreshFeed);
      });
      // Баннер при открытом приложении показывает система (`presentationOptions` в capacitor.config.ts);
      // наше дело — чтобы лента и счётчик узнали о новой строке сразу, а не через минуту.
      await PushNotifications.addListener('pushNotificationReceived', refreshFeed);
    } catch (e) {
      console.warn('[push] не удалось подписаться на нажатия:', e);
    }
  })();
}

async function openFromPush(
  data: unknown,
  router: PushRouter,
  session: PushSession,
  refreshFeed: () => void,
): Promise<void> {
  try {
    // Холодный старт: сессия ещё читается из защищённого хранилища. Судить «вошёл или нет» раньше нельзя.
    await session.ready();
    const user = session.user();
    // На телефоне вошёл сотрудник (метка осталась от ученика) — его в ученический раздел не ведём.
    if (user && user.role !== 'student') return;

    // ⚠️ «Вышел кнопкой» стирает адрес возврата (loginReturn.ts) — а нажатие на пуш как раз просит открыть
    // конкретный экран. Снимаем метку: без входа защита маршрута сама уведёт на `/login?next=…`.
    clearUserLogout();
    void router.navigate(pushTarget(data));

    const id = pushNotificationId(data);
    if (user && id) {
      // Строка ленты того же уведомления не должна висеть новой. Сбой — не беда: снимется из ленты.
      await api.post(`/notifications/read/${encodeURIComponent(id)}`).catch(() => {});
      refreshFeed();
    }
  } catch (e) {
    console.warn('[push] не удалось открыть экран по нажатию:', e);
  }
}
